import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
    formatDateTime,
    parseCustomDate,
    calculateActiveSLA,
    calculateTAT,
    calculateSLA,
    getClientSlaTargetHours
} from "../utils/helpers";

const formatWaitDuration = (dateTimeStr) => {
    if (!dateTimeStr || dateTimeStr === "-") return null;
    const parsed = parseCustomDate(dateTimeStr) || new Date(dateTimeStr);
    if (!parsed || isNaN(parsed.getTime())) return null;

    const diffMs = Date.now() - parsed.getTime();
    if (diffMs < 0) return "Just now";

    const totalMins = Math.floor(diffMs / 60000);
    if (totalMins < 1) return "Just now";
    if (totalMins < 60) return `${totalMins} mins`;

    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return mins > 0 ? `${hrs} hr ${mins} mins` : `${hrs} hr`;
};

const isClosedOrExceptionFile = (f) => {
    const statusVal = String(f.status || "").trim().toLowerCase();
    const substatus = String(f.substatus || "").trim().toLowerCase();

    return (
        statusVal === "completed" ||
        statusVal === "rejected" ||
        statusVal === "wrong report" ||
        statusVal === "do not review" ||
        statusVal === "complete by client" ||
        statusVal.includes("exception") ||
        substatus === "completed" ||
        substatus === "rejected" ||
        substatus === "wrong report" ||
        substatus === "do not review" ||
        substatus === "complete by client" ||
        substatus === "completed by client" ||
        substatus.includes("exception")
    );
};

const ReviewerDashboard = ({
    currentUser = { name: "", role: "User" },
    isManager = false,
    clientTatMap = {},
    startDate = "",
    endDate = "",
    selectedClient = "All",
    shiftFilter = "All",
    selectedLocation = "All",
    onOpenWorkspace = null
}) => {
    const [allAssignedFiles, setAllAssignedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reviewerList, setReviewerList] = useState([]);
    const [selectedReviewer, setSelectedReviewer] = useState(
        isManager ? "All" : (currentUser?.name || "")
    );
    const [statusTab, setStatusTab] = useState("Active"); // "Active" | "Completed" | "All"
    const [typeFilter, setTypeFilter] = useState("All"); // "All" | "QC" | "Revised"
    const [dateRangeFilter, setDateRangeFilter] = useState("Today"); // "Today" | "Month" | "Custom"
    const [customStartDate, setCustomStartDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    });
    const [customEndDate, setCustomEndDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    });
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 50;
    const [searchQuery, setSearchQuery] = useState("");
    const [collapsedClients, setCollapsedClients] = useState({});

    const handleQuickRange = (rangeKey) => {
        const now = new Date();
        const yearStr = now.getFullYear();
        const monthStr = String(now.getMonth() + 1).padStart(2, "0");
        const dayStr = String(now.getDate()).padStart(2, "0");
        const todayStr = `${yearStr}-${monthStr}-${dayStr}`;

        if (rangeKey === "Today") {
            setCustomStartDate(todayStr);
            setCustomEndDate(todayStr);
            setDateRangeFilter("Today");
        } else if (rangeKey === "Month") {
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            setCustomStartDate(`${yearStr}-${monthStr}-01`);
            setCustomEndDate(`${yearStr}-${monthStr}-${String(lastDay).padStart(2, "0")}`);
            setDateRangeFilter("Month");
        }
    };

    // Sync default reviewer selection when currentUser loads
    useEffect(() => {
        if (!isManager && currentUser?.name && currentUser.name !== "Waiting...") {
            setSelectedReviewer(currentUser.name);
        }
    }, [isManager, currentUser]);

    // Fetch reviewer dropdown list for managers
    useEffect(() => {
        if (!isManager) return;
        const fetchUsers = async () => {
            try {
                const res = await axios.get("/api/users/dropdown-list");
                if (Array.isArray(res.data)) {
                    setReviewerList(res.data);
                }
            } catch (err) {
                console.error("Failed to load reviewer list:", err);
            }
        };
        fetchUsers();
    }, [isManager]);

    const fetchReviewerFiles = useCallback(async (silent = false) => {
        const isSilent = silent === true;
        const token = localStorage.getItem("token");
        if (!token || !currentUser?.name || currentUser.name === "Waiting...") return;

        if (!isSilent) setLoading(true);
        try {
            const now = new Date();
            const yearStr = now.getFullYear();
            const monthStr = String(now.getMonth() + 1).padStart(2, "0");
            const dayStr = String(now.getDate()).padStart(2, "0");
            const todayStr = `${yearStr}-${monthStr}-${dayStr}`;

            const rangeStart = customStartDate || todayStr;
            const rangeEnd = customEndDate || todayStr;

            const params = {
                department: "",
                client: selectedClient && selectedClient !== "All" && selectedClient !== "Dashboard" ? selectedClient : "All",
                shiftFilter: shiftFilter || "All",
                location: selectedLocation || "All",
                startDate: rangeStart,
                endDate: rangeEnd,
                includeActive: "true"
            };

            if (!isManager) {
                params.employee = currentUser.name;
            } else if (selectedReviewer && selectedReviewer !== "All") {
                params.employee = selectedReviewer;
            }

            const res = await axios.get("/api/files/filter", {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            if (Array.isArray(res.data)) {
                setAllAssignedFiles(res.data);
            } else {
                setAllAssignedFiles([]);
            }
        } catch (err) {
            console.error("Error fetching reviewer dashboard files:", err);
        } finally {
            if (!isSilent) setLoading(false);
        }
    }, [currentUser, isManager, selectedReviewer, selectedClient, shiftFilter, selectedLocation, customStartDate, customEndDate]);

    useEffect(() => {
        fetchReviewerFiles(false);

        const baseUrl = axios.defaults.baseURL || "";
        const eventSource = new EventSource(`${baseUrl}/api/events`);
        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === "FILES_UPDATED") {
                    fetchReviewerFiles(true);
                }
            } catch (err) {
                // Ignore parse errors
            }
        };

        return () => {
            eventSource.close();
        };
    }, [fetchReviewerFiles]);

    // Enrich files with normalized client, file type (QC/Revised), and live SLA
    const processedFiles = useMemo(() => {
        return (allAssignedFiles || [])
            .filter(f => {
                const assignedName = String(f.assigned_to || f.reviewer_name || "").trim();
                if (!assignedName) return false;

                if (!isManager) {
                    return assignedName.toLowerCase() === String(currentUser.name || "").trim().toLowerCase();
                }
                if (selectedReviewer && selectedReviewer !== "All") {
                    return assignedName.toLowerCase() === String(selectedReviewer).trim().toLowerCase();
                }
                return true;
            })
            .map(f => {
                const rawDept = String(f.department || f.activeDept || "QC").trim();
                const isRevised = rawDept.toLowerCase() === "revised" || rawDept.toLowerCase() === "report writing";
                const fileType = isRevised ? "Revised" : "QC";

                const rawClient = String(f.client_name || f.activeClient || "Unknown Client").trim();
                const cleanClient = rawClient.toLowerCase() === "anm" ? "MMA" : rawClient;

                const isClosed = isClosedOrExceptionFile(f);
                let tatDisplay = f.tat || "-";
                let slaStatusDisplay = f.sla_status || "-";

                if (!isClosed && f.in_time && f.in_time !== "-") {
                    const liveSla = calculateActiveSLA(
                        f.in_time,
                        cleanClient,
                        fileType,
                        f.priority || "",
                        clientTatMap
                    );
                    tatDisplay = liveSla.tat;
                    slaStatusDisplay = liveSla.status;
                } else if (isClosed && f.in_time && f.in_time !== "-") {
                    if (f.out_time && f.out_time !== "-") {
                        if (!tatDisplay || tatDisplay === "-") {
                            tatDisplay = calculateTAT(f.in_time, f.out_time) || "-";
                        }
                        if (!slaStatusDisplay || slaStatusDisplay === "-") {
                            slaStatusDisplay = calculateSLA(
                                tatDisplay,
                                cleanClient,
                                fileType,
                                f.priority || "",
                                clientTatMap
                            ) || "-";
                        }
                    } else if (!slaStatusDisplay || slaStatusDisplay === "-") {
                        const fallbackSla = calculateActiveSLA(
                            f.in_time,
                            cleanClient,
                            fileType,
                            f.priority || "",
                            clientTatMap
                        );
                        if (!tatDisplay || tatDisplay === "-") tatDisplay = fallbackSla.tat;
                        slaStatusDisplay = fallbackSla.status;
                    }
                }

                const targetHours = getClientSlaTargetHours(
                    cleanClient,
                    fileType,
                    f.priority || "",
                    clientTatMap
                );

                const rawStatus = String(f.status || "").trim();
                const rawStatusLower = rawStatus.toLowerCase();
                const displayStatus = !isClosed && (!rawStatus || rawStatusLower === "new" || rawStatusLower.includes("not assign"))
                    ? "Assigned"
                    : (rawStatus || "Assigned");

                return {
                    ...f,
                    cleanClient,
                    fileType,
                    isClosed,
                    tatDisplay,
                    slaStatusDisplay,
                    targetHours,
                    displayStatus,
                    assignedWaitText: !isClosed ? formatWaitDuration(f.assign_time || f.in_time) : null
                };
            });
    }, [allAssignedFiles, isManager, currentUser.name, selectedReviewer, clientTatMap]);

    // Summary counters across all reviewer files
    const summaryMetrics = useMemo(() => {
        const activeList = processedFiles.filter(f => !f.isClosed);
        const completedList = processedFiles.filter(f => f.isClosed);
        const activeClientSet = new Set(activeList.map(f => f.cleanClient || "Other"));

        const qcCount = activeList.filter(f => f.fileType === "QC").length;
        const revisedCount = activeList.filter(f => f.fileType === "Revised").length;
        const withinSlaCount = activeList.filter(f =>
            String(f.slaStatusDisplay || "").toLowerCase().includes("within")
        ).length;
        const overSlaCount = activeList.filter(f => {
            const s = String(f.slaStatusDisplay || "").toLowerCase();
            return s.includes("over") || s.includes("exceed");
        }).length;

        return {
            activeTotal: activeList.length,
            activeClientCount: activeClientSet.size,
            completedTotal: completedList.length,
            allTotal: processedFiles.length,
            qcCount,
            revisedCount,
            withinSlaCount,
            overSlaCount
        };
    }, [processedFiles]);

    // Filter files by active tab, type filter, and search query
    const filteredFiles = useMemo(() => {
        return processedFiles.filter(f => {
            if (statusTab === "Active" && f.isClosed) return false;
            if (statusTab === "Completed" && !f.isClosed) return false;

            if (typeFilter !== "All" && f.fileType !== typeFilter) return false;

            if (searchQuery.trim() !== "") {
                const q = searchQuery.trim().toLowerCase();
                const fileNum = String(f.file_number || f.loan_number || "").toLowerCase();
                const addr = String(f.address || "").toLowerCase();
                const cli = String(f.cleanClient || "").toLowerCase();
                const rev = String(f.assigned_to || f.reviewer_name || "").toLowerCase();
                if (!fileNum.includes(q) && !addr.includes(q) && !cli.includes(q) && !rev.includes(q)) {
                    return false;
                }
            }

            return true;
        });
    }, [processedFiles, statusTab, typeFilter, searchQuery]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [statusTab, typeFilter, dateRangeFilter, customStartDate, customEndDate, searchQuery, selectedReviewer]);

    const totalFilteredCount = filteredFiles.length;
    const totalPages = Math.max(1, Math.ceil(totalFilteredCount / PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedFiles = useMemo(() => {
        const startIdx = (safePage - 1) * PAGE_SIZE;
        return filteredFiles.slice(startIdx, startIdx + PAGE_SIZE);
    }, [filteredFiles, safePage]);

    // Group paginated files client-wise (max 50 rows rendered at a time)
    const clientGroups = useMemo(() => {
        const map = {};
        paginatedFiles.forEach(file => {
            const clientKey = file.cleanClient || "Other";
            if (!map[clientKey]) {
                map[clientKey] = {
                    clientName: clientKey,
                    files: [],
                    qcCount: 0,
                    revisedCount: 0,
                    overSlaCount: 0
                };
            }
            map[clientKey].files.push(file);
            if (file.fileType === "QC") map[clientKey].qcCount += 1;
            else map[clientKey].revisedCount += 1;

            const s = String(file.slaStatusDisplay || "").toLowerCase();
            if (s.includes("over") || s.includes("exceed")) {
                map[clientKey].overSlaCount += 1;
            }
        });

        // Sort client groups by overSlaCount desc, then total files desc, then alphabetical
        return Object.values(map).sort((a, b) => {
            if (b.overSlaCount !== a.overSlaCount) return b.overSlaCount - a.overSlaCount;
            if (b.files.length !== a.files.length) return b.files.length - a.files.length;
            return a.clientName.localeCompare(b.clientName);
        });
    }, [paginatedFiles]);

    const toggleClientCollapse = (clientName) => {
        setCollapsedClients(prev => ({
            ...prev,
            [clientName]: !prev[clientName]
        }));
    };

    const getStatusBadgeStyle = (statusStr) => {
        const s = String(statusStr || "").trim().toLowerCase();
        if (s === "completed") return { bg: "#dcfce7", text: "#15803d", border: "#bbf7d0" };
        if (s === "rejected") return { bg: "#fee2e2", text: "#b91c1c", border: "#fecaca" };
        if (s === "working" || s === "in progress") return { bg: "#fef3c7", text: "#b45309", border: "#fde68a" };
        if (s.includes("revision")) return { bg: "#f3e8ff", text: "#7c3aed", border: "#ddd6fe" };
        return { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd" }; // Assigned
    };

    const getSlaBadgeStyle = (slaStr) => {
        const s = String(slaStr || "").trim().toLowerCase();
        if (s.includes("within")) return { bg: "#dcfce7", text: "#15803d", border: "#86efac" };
        if (s.includes("over") || s.includes("exceed")) return { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" };
        return { bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1" };
    };

    const getPriorityBadgeStyle = (priorityStr) => {
        const p = String(priorityStr || "").trim().toLowerCase();
        if (p === "vip") return { bg: "#fff1f2", text: "#e11d48", border: "#fecdd3" };
        if (p.includes("rush")) return { bg: "#fee2e2", text: "#b91c1c", border: "#fecaca" };
        if (p.includes("late") || p.includes("past")) return { bg: "#ffedd5", text: "#c2410c", border: "#fed7aa" };
        if (p.includes("due")) return { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd" };
        return { bg: "#f8fafc", text: "#475569", border: "#e2e8f0" };
    };

    return (
        <div style={{ padding: "24px 30px", width: "100%", boxSizing: "border-box" }}>
            {/* Top Header Banner */}
            <div style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "20px 24px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.05)",
                marginBottom: "20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px"
            }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ fontSize: "22px" }}>📋</span>
                        <h2 style={{ margin: 0, fontSize: "19px", fontWeight: "900", color: "#0f172a" }}>
                            {isManager
                                ? (selectedReviewer === "All" ? "Reviewers' Assigned Files Dashboard" : `Reviewer Dashboard — ${selectedReviewer}`)
                                : `My Reviewer Dashboard — ${currentUser.name}`}
                        </h2>
                    </div>
                    <p style={{ margin: "4px 0 0 32px", fontSize: "12px", color: "#64748b", fontWeight: "600" }}>
                        Client-wise assigned files with File Type (QC / Revised), In-Time, Assignment Time, Status & Live SLA visibility
                    </p>
                </div>

                {isManager && (
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "800", color: "#475569" }}>
                                Reviewer:
                            </label>
                            <select
                                value={selectedReviewer}
                                onChange={(e) => setSelectedReviewer(e.target.value)}
                                style={{
                                    padding: "7px 12px",
                                    borderRadius: "8px",
                                    border: "1px solid #cbd5e1",
                                    fontSize: "13px",
                                    fontWeight: "700",
                                    color: "#0f172a",
                                    backgroundColor: "#f8fafc",
                                    cursor: "pointer",
                                    outline: "none"
                                }}
                            >
                                <option value="All">All Reviewers</option>
                                {reviewerList.map((rev, idx) => {
                                    const rName = typeof rev === "string" ? rev : rev.name;
                                    return (
                                        <option key={idx} value={rName}>{rName}</option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* KPI Summary Cards (Matching Admin Dashboard Card CSS) */}
            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                gap: "10px",
                marginBottom: "20px"
            }}>
                {[
                    { label: "Active Assigned", count: summaryMetrics.activeTotal, color: "#3182ce", bg: "#ebf8ff" },
                    { label: "QC Files", count: summaryMetrics.qcCount, color: "#00a3e0", bg: "#e6f7ff" },
                    { label: "Revised Files", count: summaryMetrics.revisedCount, color: "#7c3aed", bg: "#f5f3ff" },
                    { label: "Within SLA", count: summaryMetrics.withinSlaCount, color: "#2f855a", bg: "#f0fff4" },
                    { label: "Over SLA", count: summaryMetrics.overSlaCount, color: "#e53e3e", bg: "#fff5f5" },
                    { label: "Completed", count: summaryMetrics.completedTotal, color: "#38a169", bg: "#f0fff4" }
                ].map((card, idx) => (
                    <div
                        key={idx}
                        style={{
                            backgroundColor: card.bg,
                            borderRadius: "10px",
                            padding: "15px",
                            border: `1px solid ${card.color}20`,
                            textAlign: "center"
                        }}
                    >
                        <div style={{ fontSize: "20px", fontWeight: "900", color: card.color }}>{card.count}</div>
                        <div style={{ fontSize: "10px", color: "#666", marginTop: "5px" }}>{card.label}</div>
                    </div>
                ))}
            </div>

            {/* Filter Controls Bar */}
            <div style={{
                backgroundColor: "white",
                borderRadius: "10px",
                padding: "14px 20px",
                border: "1px solid #e2e8f0",
                marginBottom: "20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px"
            }}>
                {/* Status Tabs */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    {[
                        { key: "Active", label: `Active Queue (${summaryMetrics.activeTotal})` },
                        { key: "Completed", label: `Completed (${summaryMetrics.completedTotal})` },
                        { key: "All", label: `All Files (${summaryMetrics.allTotal})` }
                    ].map(tab => {
                        const isActive = statusTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setStatusTab(tab.key)}
                                style={{
                                    padding: "7px 14px",
                                    borderRadius: "8px",
                                    fontSize: "12px",
                                    fontWeight: "800",
                                    cursor: "pointer",
                                    border: isActive ? "1px solid #1d4ed8" : "1px solid #cbd5e1",
                                    backgroundColor: isActive ? "#2563eb" : "#f8fafc",
                                    color: isActive ? "white" : "#475569",
                                    transition: "all 0.15s ease"
                                }}
                            >
                                {tab.label}
                            </button>
                        );
                    })}

                    <div style={{ width: "1px", height: "22px", backgroundColor: "#e2e8f0", margin: "0 6px" }} />

                    {/* File Type Pills (QC / Revised) */}
                    {["All", "QC", "Revised"].map(type => {
                        const isActive = typeFilter === type;
                        const activeColor = type === "QC" ? "#0284c7" : (type === "Revised" ? "#7c3aed" : "#0f172a");
                        return (
                            <button
                                key={type}
                                type="button"
                                onClick={() => setTypeFilter(type)}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: "800",
                                    cursor: "pointer",
                                    border: isActive ? `1px solid ${activeColor}` : "1px solid #e2e8f0",
                                    backgroundColor: isActive ? activeColor : "white",
                                    color: isActive ? "white" : "#64748b"
                                }}
                            >
                                {type === "All" ? "All Types" : `${type} Only`}
                            </button>
                        );
                    })}

                    <div style={{ width: "1px", height: "22px", backgroundColor: "#e2e8f0", margin: "0 6px" }} />

                    {/* Date Range Pills (Today / This Month) + Custom From/To Date Pickers */}
                    {[
                        { key: "Today", label: "Today" },
                        { key: "Month", label: "This Month" }
                    ].map(range => {
                        const isActive = dateRangeFilter === range.key;
                        return (
                            <button
                                key={range.key}
                                type="button"
                                onClick={() => handleQuickRange(range.key)}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: "800",
                                    cursor: "pointer",
                                    border: isActive ? "1px solid #00a3e0" : "1px solid #cbd5e1",
                                    backgroundColor: isActive ? "#00a3e0" : "white",
                                    color: isActive ? "white" : "#475569"
                                }}
                            >
                                {range.label}
                            </button>
                        );
                    })}

                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "4px" }}>
                        <label style={{ fontSize: "11px", fontWeight: "800", color: "#64748b" }}>From:</label>
                        <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => {
                                setCustomStartDate(e.target.value);
                                setDateRangeFilter("Custom");
                            }}
                            style={{
                                padding: "5px 8px",
                                borderRadius: "6px",
                                border: dateRangeFilter === "Custom" ? "1px solid #00a3e0" : "1px solid #cbd5e1",
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#0f172a",
                                backgroundColor: "white",
                                outline: "none"
                            }}
                        />
                        <label style={{ fontSize: "11px", fontWeight: "800", color: "#64748b" }}>To:</label>
                        <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => {
                                setCustomEndDate(e.target.value);
                                setDateRangeFilter("Custom");
                            }}
                            style={{
                                padding: "5px 8px",
                                borderRadius: "6px",
                                border: dateRangeFilter === "Custom" ? "1px solid #00a3e0" : "1px solid #cbd5e1",
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#0f172a",
                                backgroundColor: "white",
                                outline: "none"
                            }}
                        />
                    </div>
                </div>

                {/* Search Input */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <input
                        type="text"
                        placeholder="Search File #, Client, Address..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            padding: "7px 14px",
                            borderRadius: "8px",
                            border: "1px solid #cbd5e1",
                            fontSize: "12px",
                            minWidth: "240px",
                            outline: "none"
                        }}
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => setSearchQuery("")}
                            style={{
                                padding: "6px 10px",
                                borderRadius: "6px",
                                border: "1px solid #cbd5e1",
                                backgroundColor: "#f8fafc",
                                fontSize: "11px",
                                fontWeight: "700",
                                cursor: "pointer"
                            }}
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Client-Wise Categorized Sections */}
            {loading ? (
                <div style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    padding: "48px",
                    textAlign: "center",
                    border: "1px solid #e2e8f0",
                    color: "#64748b",
                    fontWeight: "700"
                }}>
                    Loading assigned files...
                </div>
            ) : clientGroups.length === 0 ? (
                <div style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    padding: "48px",
                    textAlign: "center",
                    border: "1px solid #e2e8f0"
                }}>
                    <div style={{ fontSize: "32px", marginBottom: "8px" }}>✅</div>
                    <div style={{ fontSize: "15px", fontWeight: "800", color: "#1e293b" }}>
                        No files found for the selected view
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                        {statusTab === "Active"
                            ? "There are currently no active (Assigned / Working) files in this queue."
                            : "Try switching the filter tabs above or adjusting the date range."}
                    </div>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                    {clientGroups.map(group => {
                        const isCollapsed = !!collapsedClients[group.clientName];
                        return (
                            <div
                                key={group.clientName}
                                style={{
                                    backgroundColor: "white",
                                    borderRadius: "12px",
                                    border: group.overSlaCount > 0 ? "1px solid #fecaca" : "1px solid #e2e8f0",
                                    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
                                    overflow: "hidden"
                                }}
                            >
                                {/* Client Group Header */}
                                <div
                                    onClick={() => toggleClientCollapse(group.clientName)}
                                    style={{
                                        padding: "14px 20px",
                                        backgroundColor: group.overSlaCount > 0 ? "#fff5f5" : "#f8fafc",
                                        borderBottom: isCollapsed ? "none" : "1px solid #e2e8f0",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        flexWrap: "wrap",
                                        gap: "10px"
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <span style={{ fontSize: "13px", fontWeight: "900", color: "#475569", width: "16px" }}>
                                            {isCollapsed ? "▶" : "▼"}
                                        </span>
                                        <span style={{ fontSize: "16px" }}>🏢</span>
                                        <span style={{ fontSize: "15px", fontWeight: "900", color: "#0f172a" }}>
                                            {group.clientName}
                                        </span>
                                        <span style={{
                                            backgroundColor: "#e2e8f0",
                                            color: "#1e293b",
                                            borderRadius: "999px",
                                            padding: "2px 10px",
                                            fontSize: "12px",
                                            fontWeight: "800"
                                        }}>
                                            {group.files.length} {group.files.length === 1 ? "File" : "Files"}
                                        </span>
                                    </div>

                                    {/* Client Header Summary Badges */}
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                        {group.qcCount > 0 && (
                                            <span style={{
                                                backgroundColor: "#e0f2fe",
                                                color: "#0369a1",
                                                border: "1px solid #bae6fd",
                                                borderRadius: "6px",
                                                padding: "3px 10px",
                                                fontSize: "11px",
                                                fontWeight: "800"
                                            }}>
                                                QC: {group.qcCount}
                                            </span>
                                        )}
                                        {group.revisedCount > 0 && (
                                            <span style={{
                                                backgroundColor: "#f3e8ff",
                                                color: "#7c3aed",
                                                border: "1px solid #ddd6fe",
                                                borderRadius: "6px",
                                                padding: "3px 10px",
                                                fontSize: "11px",
                                                fontWeight: "800"
                                            }}>
                                                Revised: {group.revisedCount}
                                            </span>
                                        )}
                                        {group.overSlaCount > 0 && (
                                            <span style={{
                                                backgroundColor: "#fee2e2",
                                                color: "#b91c1c",
                                                border: "1px solid #fca5a5",
                                                borderRadius: "6px",
                                                padding: "3px 10px",
                                                fontSize: "11px",
                                                fontWeight: "800"
                                            }}>
                                                ⚠️ {group.overSlaCount} Over SLA
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Client Files Table */}
                                {!isCollapsed && (
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", tableLayout: "fixed", minWidth: "900px" }}>
                                            <thead>
                                                <tr style={{ backgroundColor: "#ffffff", borderBottom: "2px solid #f1f5f9" }}>
                                                    <th style={{ width: isManager && selectedReviewer === "All" ? "15%" : "17%", padding: "11px 16px", textAlign: "left", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>File Number</th>
                                                    {isManager && selectedReviewer === "All" && (
                                                        <th style={{ width: "12%", padding: "11px 16px", textAlign: "left", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Reviewer</th>
                                                    )}
                                                    <th style={{ width: "9%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>File Type</th>
                                                    <th style={{ width: "10%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Priority</th>
                                                    <th style={{ width: "14%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>File In-Time</th>
                                                    <th style={{ width: "16%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Assign Time</th>
                                                    <th style={{ width: "11%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>File Status</th>
                                                    <th style={{ width: "14%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>TAT & SLA Visibility</th>
                                                    {onOpenWorkspace && (
                                                        <th style={{ width: "9%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Workspace</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.files.map((file, idx) => {
                                                    const statusStyle = getStatusBadgeStyle(file.displayStatus);
                                                    const slaStyle = getSlaBadgeStyle(file.slaStatusDisplay);
                                                    const prioStyle = getPriorityBadgeStyle(file.priority);
                                                    const isOverSla = String(file.slaStatusDisplay || "").toLowerCase().includes("over") ||
                                                        String(file.slaStatusDisplay || "").toLowerCase().includes("exceed");

                                                    return (
                                                        <tr
                                                            key={file.id || idx}
                                                            style={{
                                                                borderBottom: "1px solid #f1f5f9",
                                                                backgroundColor: !file.isClosed && isOverSla ? "#fffafa" : "white"
                                                            }}
                                                        >
                                                            {/* File Number */}
                                                            <td style={{ padding: "12px 16px", fontWeight: "800", color: "#0f172a" }}>
                                                                <div>{file.file_number || file.loan_number || "-"}</div>
                                                                {file.address && file.address !== "-" && (
                                                                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "500", marginTop: "2px", maxWidth: "240px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={file.address}>
                                                                        {file.address}
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* Reviewer (when Manager views All) */}
                                                            {isManager && selectedReviewer === "All" && (
                                                                <td style={{ padding: "12px 16px", fontWeight: "700", color: "#334155" }}>
                                                                    {file.assigned_to || file.reviewer_name || "-"}
                                                                </td>
                                                            )}

                                                            {/* File Type: QC or Revised */}
                                                            <td style={{ padding: "12px 16px", textAlign: "center" }}>
                                                                <span style={{
                                                                    display: "inline-block",
                                                                    padding: "4px 10px",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px",
                                                                    fontWeight: "900",
                                                                    backgroundColor: file.fileType === "Revised" ? "#f3e8ff" : "#e0f2fe",
                                                                    color: file.fileType === "Revised" ? "#7c3aed" : "#0369a1",
                                                                    border: file.fileType === "Revised" ? "1px solid #ddd6fe" : "1px solid #bae6fd"
                                                                }}>
                                                                    {file.fileType}
                                                                </span>
                                                            </td>

                                                            {/* Priority */}
                                                            <td style={{ padding: "12px 16px", textAlign: "center" }}>
                                                                <span style={{
                                                                    display: "inline-block",
                                                                    padding: "3px 9px",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px",
                                                                    fontWeight: "800",
                                                                    backgroundColor: prioStyle.bg,
                                                                    color: prioStyle.text,
                                                                    border: `1px solid ${prioStyle.border}`
                                                                }}>
                                                                    {file.priority || "Standard"}
                                                                </span>
                                                            </td>

                                                            {/* File In-Time */}
                                                            <td style={{ padding: "12px 16px", textAlign: "center", fontWeight: "600", color: "#334155", whiteSpace: "nowrap" }}>
                                                                {formatDateTime(file.in_time) || file.in_time || "-"}
                                                            </td>

                                                            {/* Assignment Time */}
                                                            <td style={{ padding: "12px 16px", textAlign: "center", whiteSpace: "nowrap" }}>
                                                                <div style={{ fontWeight: "600", color: "#334155" }}>
                                                                    {formatDateTime(file.assign_time) || file.assign_time || "-"}
                                                                </div>
                                                                {file.assignedWaitText && (
                                                                    <div style={{ fontSize: "11px", fontWeight: "800", color: "#2563eb", marginTop: "2px" }}>
                                                                        ⏱️ Assigned {file.assignedWaitText} ago
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* File Status */}
                                                            <td style={{ padding: "12px 16px", textAlign: "center" }}>
                                                                <span style={{
                                                                    display: "inline-block",
                                                                    padding: "4px 10px",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px",
                                                                    fontWeight: "800",
                                                                    backgroundColor: statusStyle.bg,
                                                                    color: statusStyle.text,
                                                                    border: `1px solid ${statusStyle.border}`
                                                                }}>
                                                                    {file.displayStatus}
                                                                </span>
                                                            </td>

                                                            {/* SLA Visibility */}
                                                            <td style={{ padding: "12px 16px", textAlign: "center", whiteSpace: "nowrap" }}>
                                                                <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                                                    <span style={{
                                                                        padding: "4px 10px",
                                                                        borderRadius: "6px",
                                                                        fontSize: "11px",
                                                                        fontWeight: "900",
                                                                        backgroundColor: slaStyle.bg,
                                                                        color: slaStyle.text,
                                                                        border: `1px solid ${slaStyle.border}`
                                                                    }}>
                                                                        {file.slaStatusDisplay !== "-" ? file.slaStatusDisplay : "Pending"}
                                                                    </span>
                                                                    <span style={{
                                                                        fontSize: "12px",
                                                                        fontWeight: "800",
                                                                        color: "#1e293b"
                                                                    }}>
                                                                        ({file.tatDisplay || "-"})
                                                                    </span>
                                                                </div>
                                                                <div style={{ fontSize: "10px", color: "#64748b", marginTop: "3px", fontWeight: "600" }}>
                                                                    SLA Target: {file.targetHours}h
                                                                </div>
                                                            </td>

                                                            {/* Open Workspace Action */}
                                                            {onOpenWorkspace && (
                                                                <td style={{ padding: "12px 16px", textAlign: "center" }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => onOpenWorkspace(group.clientName, file.fileType)}
                                                                        style={{
                                                                            padding: "5px 10px",
                                                                            borderRadius: "6px",
                                                                            border: "1px solid #bfdbfe",
                                                                            backgroundColor: "#eff6ff",
                                                                            color: "#1d4ed8",
                                                                            fontSize: "11px",
                                                                            fontWeight: "800",
                                                                            cursor: "pointer",
                                                                            whiteSpace: "nowrap"
                                                                        }}
                                                                    >
                                                                        Open {file.fileType} →
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Pagination Bar (50 rows per page) */}
                    {totalFilteredCount > PAGE_SIZE && (
                        <div style={{
                            backgroundColor: "white",
                            borderRadius: "10px",
                            padding: "12px 20px",
                            border: "1px solid #e2e8f0",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "12px"
                        }}>
                            <span style={{ fontSize: "12px", fontWeight: "700", color: "#475569" }}>
                                Showing <b>{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, totalFilteredCount)}</b> of <b>{totalFilteredCount}</b> files ({PAGE_SIZE} per page)
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <button
                                    type="button"
                                    disabled={safePage <= 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    style={{
                                        padding: "6px 12px",
                                        borderRadius: "6px",
                                        border: "1px solid #cbd5e1",
                                        backgroundColor: safePage <= 1 ? "#f1f5f9" : "white",
                                        color: safePage <= 1 ? "#94a3b8" : "#0f172a",
                                        fontSize: "12px",
                                        fontWeight: "800",
                                        cursor: safePage <= 1 ? "not-allowed" : "pointer"
                                    }}
                                >
                                    ← Prev
                                </button>
                                <span style={{ fontSize: "12px", fontWeight: "800", color: "#0f172a", padding: "0 8px" }}>
                                    Page {safePage} of {totalPages}
                                </span>
                                <button
                                    type="button"
                                    disabled={safePage >= totalPages}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    style={{
                                        padding: "6px 12px",
                                        borderRadius: "6px",
                                        border: "1px solid #cbd5e1",
                                        backgroundColor: safePage >= totalPages ? "#f1f5f9" : "white",
                                        color: safePage >= totalPages ? "#94a3b8" : "#0f172a",
                                        fontSize: "12px",
                                        fontWeight: "800",
                                        cursor: safePage >= totalPages ? "not-allowed" : "pointer"
                                    }}
                                >
                                    Next →
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReviewerDashboard;
